/**
 * MIA AI Internationalization (i18n) Module
 * Now syncs with global i18n system from src/plugins/i18n.js
 * Uses localStorage 'locale' key (same as global system)
 */

import { computed, watch } from "vue";
import i18n from "@/plugins/i18n";

// Get current locale from global i18n
const getGlobalLocale = () => {
  return i18n.global.locale.value || localStorage.getItem("locale") || "ar";
};

/**
 * Get current locale (synced with global)
 */
export const currentLocale = computed(() => getGlobalLocale());

/**
 * Check if current locale is RTL
 */
export const isRtl = computed(() => getGlobalLocale() === "ar");

/**
 * Set locale - delegates to global i18n system
 * @param {string} locale - 'en' or 'ar'
 */
export function setLocale(locale) {
  if (["ar", "en"].includes(locale)) {
    // Use global switchLocale if available, otherwise set directly
    if (typeof window !== "undefined") {
      const { switchLocale } = require("@/plugins/i18n");
      if (switchLocale) {
        switchLocale(locale);
      } else {
        i18n.global.locale.value = locale;
        localStorage.setItem("locale", locale);
      }
    }
  }
}

/**
 * Translate a key using global i18n with mia. prefix
 * @param {string} key - Translation key (without mia. prefix)
 * @param {Object} params - Optional parameters for interpolation
 * @returns {string}
 */
export function t(key, params = {}) {
  // Try mia.key first, then fallback to key directly
  const miaKey = `mia.${key}`;
  let text = i18n.global.t(miaKey);

  // If translation returns the key itself, try without prefix
  if (text === miaKey) {
    text = i18n.global.t(key);
  }

  // If still returns key, return just the key name
  if (text === key || text === miaKey) {
    return key;
  }

  // Simple parameter interpolation for {param} style
  if (params && Object.keys(params).length > 0) {
    Object.entries(params).forEach(([param, value]) => {
      text = text.replace(new RegExp(`{${param}}`, "g"), value);
    });
  }

  return text;
}

/**
 * Vue composable for i18n
 */
export function useI18n() {
  return {
    t,
    locale: currentLocale,
    isRtl,
    setLocale,
    availableLocales: ["en", "ar"],
  };
}

/**
 * Vue plugin for global access (now delegates to global i18n)
 */
export const i18nPlugin = {
  install(app) {
    // Use global $t but wrap it for mia. prefix support
    app.config.globalProperties.$miaT = t;
    app.config.globalProperties.$miaLocale = currentLocale;
    app.config.globalProperties.$miaIsRtl = isRtl;
    app.config.globalProperties.$miaSetLocale = setLocale;
  },
};

export default {
  t,
  currentLocale,
  isRtl,
  setLocale,
  useI18n,
  i18nPlugin,
};
